// src/components/common/Layout.tsx
import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';
import {
  HomeIcon,
  TrophyIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/app', icon: HomeIcon },
  { name: 'My Leagues', href: '/app/leagues', icon: TrophyIcon },
  { name: 'Open Leagues', href: '/app/leagues/browse', icon: MagnifyingGlassIcon },
  { name: 'Players', href: '/app/players', icon: UsersIcon },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isNavActive = (href: string) =>
    location.pathname === href || (href !== '/app' && location.pathname.startsWith(href));

  // Record-book nav: square, uppercase tracked labels (the "table of contents"),
  // ink block + gold icon as the active marker. Mobile keeps a 44px touch target.
  const navItemClass = (active: boolean, mobile: boolean) =>
    `group flex items-center gap-3 px-3 ${mobile ? 'py-3' : 'py-2.5'} text-sm font-semibold uppercase tracking-wider transition-colors ${
      active ? 'bg-ink-900 text-paper' : 'text-secondary-600 hover:bg-secondary-100 hover:text-ink-900'
    }`;

  const navIconClass = (active: boolean) =>
    `h-5 w-5 flex-shrink-0 ${active ? 'text-gold' : 'text-secondary-400 group-hover:text-secondary-600'}`;

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-secondary-600 bg-opacity-75" />
        </div>
      )}

      {/* Mobile sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:hidden
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-4 border-b-2 border-ink-900">
          <div className="flex items-center">
            <span className="font-display text-2xl tracking-tight leading-none text-secondary-900">
              ONLY<span className="text-gold-700">KICKERS</span>
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="icon-btn p-2 text-secondary-400 hover:text-ink-900"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <nav className="mt-4 px-2 space-y-1">
          {navigation.map((item) => {
            const isActive = isNavActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={navItemClass(isActive, true)}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={navIconClass(isActive)} />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex-1 flex flex-col min-h-0 bg-white">
          <div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-ink-900">
            <span className="font-display text-2xl tracking-tight leading-none text-secondary-900">
              ONLY<span className="text-gold-700">KICKERS</span>
            </span>
          </div>
          <div className="flex-1 flex flex-col overflow-y-auto border-r border-secondary-200">
            <nav className="flex-1 px-2 py-4 space-y-1">
              {navigation.map((item) => {
                const isActive = isNavActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={navItemClass(isActive, false)}
                  >
                    <item.icon className={navIconClass(isActive)} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1 min-h-screen">
        {/* Top header */}
        <div className="sticky top-0 z-10 bg-white">
          <div className="flex justify-between items-center px-4 sm:px-6 lg:px-8 h-16 border-b border-ink-900">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="icon-btn md:hidden p-2 text-secondary-400 hover:text-ink-900 hover:bg-secondary-100"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>

            {/* Page title - could be dynamic based on route */}
            <div className="flex-1 md:flex-none">
              <h1 className="font-display text-xl tracking-tight text-secondary-900 md:hidden">
                ONLY<span className="text-gold-700">KICKERS</span>
              </h1>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <NotificationBell />

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="icon-btn flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ink-900"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gold flex items-center justify-center">
                      <span className="font-display text-base leading-none text-secondary-900">
                        {user?.displayName?.charAt(0) || user?.username?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-ink-900">
                        {user?.displayName || user?.username}
                      </p>
                      <p className="text-xs text-secondary-500">{user?.email}</p>
                    </div>
                    <ChevronDownIcon className="w-4 h-4 text-secondary-400" />
                  </div>
                </button>

                {/* User dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-ink-900 shadow-sm z-20">
                    <div className="px-4 py-2 text-sm text-secondary-700 border-b border-secondary-200 md:hidden">
                      <p className="font-medium text-ink-900">{user?.displayName || user?.username}</p>
                      <p className="text-secondary-500">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="block w-full text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-secondary-700 hover:bg-ink-900 hover:text-paper transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>

        <footer className="border-t border-secondary-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 text-xs text-secondary-400">
              <span>&copy; 2026 OnlyKickers</span>
              <span className="hidden sm:inline">·</span>
              <Link to="/privacy" className="hover:text-secondary-600">Privacy Policy</Link>
              <span className="hidden sm:inline">·</span>
              <Link to="/terms" className="hover:text-secondary-600">Terms of Service</Link>
              <span className="hidden sm:inline">·</span>
              <a href="mailto:support@onlykickers.com" className="hover:text-secondary-600">support@onlykickers.com</a>
            </div>
          </div>
        </footer>
      </div>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div 
          className="fixed inset-0 z-0"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </div>
  );
}
